/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useMemo } from "react";
import {
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Button,
  Box,
  Typography,
  Fade,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { RoleFolderPermissionDto } from "../../types/Role";

interface User {
  userId: number;
  userName: string;
  isChecked: boolean;
}

interface Props {
  value: User[];
  row: RoleFolderPermissionDto;
  userId: number;
  onUserChange: (roleId: number, selectedUserIds: number[]) => void;
  disabled?: boolean;
}

const ITEM_HEIGHT = 40;

const UserMultiSelectCell: React.FC<Props> = ({ value = [], row, userId, onUserChange, disabled = false, }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState("");

  const selectedUserIds = useMemo(
    () => value.filter((u) => u.isChecked).map((u) => u.userId),
    [value]
  );

  const filteredUsers = useMemo(
    () => value.filter((user) => user.userName?.toLowerCase().includes(searchText?.toLowerCase())),
    [value, searchText]
  );

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUserIds.includes(user.userId) || user.userId === userId);

  const handleSelectAllClick = (checked: boolean) => {
    const updatedIds = checked
      ? Array.from(
          new Set([
            ...selectedUserIds,
            ...filteredUsers.map((u) => u.userId).filter((id) => id !== userId),
          ])
        )
      : selectedUserIds.filter(
          (id) => !filteredUsers.some((user) => user.userId === id && user.userId !== userId)
        );

    onUserChange(row.roleId, updatedIds);
  };

  const handleItemToggle = (id: number) => {
    const updated = selectedUserIds.includes(id)
      ? selectedUserIds.filter((uid) => uid !== id)
      : [...selectedUserIds, id];

    onUserChange(row.roleId, updated);
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSearchText("");
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const selectedText =
    value
      .filter((u) => selectedUserIds.includes(u.userId))
      .map((u) => u.userName)
      .join(", ") || "Select users";

  /** Virtualized Row Renderer */
  const Row = ({ index, style }: ListChildComponentProps) => {
    if (index === 0) {
      // "Select All" row
      return (
        <div style={style}>
          <Box
            display="flex"
            alignItems="center"
            px={1}
            onClick={() => handleSelectAllClick(!isAllSelected)}
            sx={{ cursor: "pointer" }}
          >
            <Checkbox
              size="small"
              checked={isAllSelected}
              indeterminate={
                !isAllSelected &&
                filteredUsers.some((user) => selectedUserIds.includes(user.userId))
              }
            />
            <ListItemText
              primary={
                <Typography fontWeight="bold" fontSize="0.875rem">
                  Select All
                </Typography>
              }
            />
          </Box>
        </div>
      );
    }

    const user = filteredUsers[index - 1]; // -1 because first row is "Select All"
    return (
      <div style={style}>
        <Box
          display="flex"
          alignItems="center"
          px={1}
          sx={{ cursor: user.userId !== userId ? "pointer" : "default" }}
          onClick={() => user.userId !== userId && handleItemToggle(user.userId)}
        >
          <Checkbox
            size="small"
            checked={selectedUserIds.includes(user.userId)}
            disabled={user.userId === userId}
          />
          <ListItemText primary={<Typography fontSize="0.85rem">{user.userName}</Typography>} />
        </Box>
      </div>
    );
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        endIcon={<ArrowDropDownIcon />}
        variant="outlined"
        size="small"
        fullWidth
        disabled={disabled} 
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          color: "text.secondary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          outline: "none",
          border: "1px solid rgba(0, 0, 0, 0.6)",
          borderRadius: 1,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flexGrow: 1,
            textAlign: "left",
            color: "rgba(0, 0, 0, 0.6)",
          }}
          title={selectedText}
        >
          {selectedText}
        </span>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            style: {
              minWidth: anchorEl?.clientWidth || 200,
              overflow: "hidden",
            },
            sx: { mt: 0.5 },
          },
        }}
      >
        <Box px={1} pb={0.5}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchText}
            sx={{ mt: 0.5 }}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchText("")}
                    edge="end"
                    tabIndex={-1}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Virtualized List */}
        <FixedSizeList
          height={300}
          width="100%"
          itemCount={filteredUsers.length + 1} // +1 for "Select All"
          itemSize={ITEM_HEIGHT}
          overscanCount={5}
        >
          {Row}
        </FixedSizeList>
      </Popover>
    </>
  );
};

export default UserMultiSelectCell;
